// src/App.js
import React, { useEffect, useMemo, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import "./App.css";
import { api } from "./scripts/api";

import {
  Boxes,
  Car,
  Warehouse,
  BadgeDollarSign,
  ListChecks,
  ScanLine,
  Truck,
  PlusCircle,
  Shield,
  BarChart3,
  Database,
  LogIn,
  LogOut,
  UsersRound,
  LucideDollarSign,
} from "lucide-react";

import NavBar from './components/NavBar';
import InventoryPage from './pages/InventoryPage';
import SoldPage from './pages/SoldPage';
import VinScanPage from './pages/VinScanPage';
import PdiPage from './pages/PdiPage';
import PdiSelectPage from './pages/PdiSelectPage';
import PdiTemplatePage from './pages/PdiTemplatePage';
import InventoryImportPage from './pages/InventoryImportPage';
import DriverPage from './pages/DriverPage';
import AdminPage from './pages/AdminPage';
import NewRecordPage from './pages/NewRecordPage';
import DetailPage from './pages/DetailPage';
import DemoPage from './pages/DemoPage';
import StockPage from './pages/StockPage';
import RentalPage from './pages/RentalPage';
import BuilderUploadPage from "./pages/BuilderUploadPage";
import ReportsPage from "./pages/ReportsPage_old";
import AdminSchemaPage from "./pages/AdminSchemaPage";
import UsersPage from "./pages/UsersPage";
import DatabaseSettingsPage from "./pages/DatabaseSettingsPage";
import PreLoginSplashPage from "./pages/PreLoginSplashPage";
import MovesPage from "./pages/MovesPage";
import DriverMgmtPage from './pages/DriverMgmtPage';
import AdminCustomersPage from "./pages/AdminCustomersPage";
import AdminVendorsPage from "./pages/AdminVendorsPage";
import CustomerDetailsPage from "./pages/CustomerDetailsPage";
import AdminApiKeysPage from "./pages/AdminApiKeysPage";
import ActiveDesktop from "./pages/ActiveDesktop";
import SchedulerPage from "./pages/SchedulerPage";
import DeveloperToolsPage from "./pages/DeveloperToolsPage";

export const ThemeCtx = React.createContext({ theme: "system", setTheme: () => {} });

function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => localStorage.getItem("sna_theme") || "system");

  useEffect(() => {
    const root = document.documentElement;
    if (theme === "system") root.removeAttribute("data-theme");
    else root.setAttribute("data-theme", theme);
    localStorage.setItem("sna_theme", theme);
  }, [theme]);

  return <ThemeCtx.Provider value={{ theme, setTheme }}>{children}</ThemeCtx.Provider>;
}

function hasToken() {
const t = sessionStorage.getItem("auth_token")?.trim() || "";
return !!t;
}

function hasDbSelected() {
  const d = sessionStorage.getItem("active_db") || "";
  return !!d;
}

function AuthGate({ children }) {
  const loc = useLocation();
  const nav = useNavigate();

  const path = loc.pathname;

  const tokenOk = hasToken();
  const dbOk = hasDbSelected();

  const isPublic =
    path === "/" ||
    path === "/login" ||
    path === "/db-select";

  // If not public, enforce token + db
  useEffect(() => {
    if (isPublic) return;

    if (!tokenOk) {
      nav("/login", { replace: true });
      return;
    }
    if (!dbOk) {
      nav("/db-select", { replace: true });
      return;
    }
  }, [isPublic, tokenOk, dbOk, nav]);

  // 🚫 Block protected UI from rendering until checks pass
  if (!isPublic) {
    if (!tokenOk) return null;
    if (!dbOk) return null;
  }

  return children;
}


function LoginPage() {
  const nav = useNavigate();
  const [email, setEmail] = useState(sessionStorage.getItem("last_email") || "");
  const [password, setPassword] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setMsg("");
    try {
      const { data } = await api.post("/auth/login", { email, password });

sessionStorage.setItem("auth_token", data?.token || "");
sessionStorage.setItem("auth_user", JSON.stringify(data?.user || null));
sessionStorage.setItem("last_email", email);

// set active_db automatically if missing
if (!sessionStorage.getItem("active_db") && data?.defaultDbId) {
  sessionStorage.setItem("active_db", data.defaultDbId);
}

nav("/inventory", { replace: true });
    } catch (e) {
      setMsg(e?.response?.data?.error || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="container">
      <div className="card" style={{ maxWidth: 520, margin: "24px auto", minWidth: 0 }}>
        <div className="row" style={{ justifyContent: "space-between", alignItems: "center" }}>
          <h2 style={{ margin: 0, fontWeight: 900, letterSpacing: 0.2 }}>Login</h2>
          <LogIn size={18} />
        </div>

        {msg ? <div className="muted" style={{ marginTop: 8 }}>{msg}</div> : null}

        <div style={{ marginTop: 12, display: "grid", gap: 10 }}>
          <div>
            <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 6 }}>Email</div>
            <input className="input" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>

          <div>
            <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 6 }}>Password</div>
            <input className="input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
          </div>

          <button className="btn" onClick={submit} disabled={busy}>
            {busy ? "Signing in…" : "Sign in"}
          </button>

          <div className="muted" style={{ marginTop: 6, lineHeight: 1.6 }}>
            Admin can create users and grant DB permissions (read/write/admin).
            Demo Username: admin@example.com
            Demo Password: Admin123
          </div>
        </div>
      </div>
    </div>
  );
}

function getAuthUser() {
  try { return JSON.parse(sessionStorage.getItem("auth_user") || "null"); }
  catch { return null; }
}

function DbSelectPage() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  const [newName, setNewName] = useState("");

  const load = async () => {
    setBusy(true);
    setMsg("");
    try {
      const { data } = await api.get("/db/list");
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setMsg(e?.response?.data?.error || "Failed to list databases");
      setRows([]);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const choose = (dbId) => {
    sessionStorage.setItem("active_db", dbId);
    nav("/inventory", { replace: true });
  };

  const createDb = async () => {
    const name = String(newName || "").trim();
    if (!name) return;

    setBusy(true);
    setMsg("");
    try {
      const { data } = await api.post("/db/create", {
        name,
        template: "inventory",
        extras: {
          finance: true,
          purchase_orders: true,
          tracking: true,
          date_tracking: true,
        },
      });
      const dbId = data?.dbId;
      if (dbId) {
        sessionStorage.setItem("active_db", dbId);
        nav("/inventory", { replace: true });
      } else {
        setMsg("Created DB but did not get dbId back");
        load();
      }
    } catch (e) {
      setMsg(e?.response?.data?.error || "Failed to create database");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="container">
      <div className="row" style={{ justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
        <div>
          <h2 style={{ margin: 0, fontWeight: 900, letterSpacing: 0.2 }}>Select Database</h2>
          <div className="muted" style={{ marginTop: 4 }}>
            Your requests will target this DB (sent via <code>X-DB</code> header).
          </div>
        </div>

        <div className="row" style={{ gap: 8 }}>
          <button
            className="btn-outline"
            onClick={() => {
              sessionStorage.removeItem("auth_token");
              sessionStorage.removeItem("active_db");
              nav("/login", { replace: true });
            }}
          >
            <LogOut size={16} style={{ marginRight: 8 }} />
            Logout
          </button>
        </div>
      </div>

      {msg ? <div className="muted" style={{ marginTop: 10 }}>{msg}</div> : null}

      <div className="card" style={{ marginTop: 12, minWidth: 0 }}>
        <div className="row" style={{ justifyContent: "space-between", alignItems: "center", gap: 10 }}>
          <div style={{ fontWeight: 900 }}>Create new DB</div>
          <Database size={18} />
        </div>

        <div className="row" style={{ gap: 10, marginTop: 10, flexWrap: "wrap" }}>
          <input
            className="input"
            placeholder="Database name (e.g. SNA 2026)"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            style={{ minWidth: 260 }}
          />
          <button className="btn" onClick={createDb} disabled={busy || !newName.trim()}>
            Create
          </button>
          <button className="btn-outline" onClick={load} disabled={busy}>
            Refresh
          </button>
        </div>

        <div className="muted" style={{ marginTop: 8 }}>
          Template: inventory + finance + PO + tracking + date tracking packs enabled by default.
        </div>
      </div>

      <div className="card" style={{ marginTop: 12, minWidth: 0 }}>
        <div style={{ fontWeight: 900, marginBottom: 8 }}>Available databases</div>

        {busy ? <div className="muted">Loading…</div> : null}
        {!busy && rows.length === 0 ? <div className="muted">No databases found.</div> : null}

        <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
          {rows.map((r) => (
            <button key={r.dbId} className="btn" onClick={() => choose(r.dbId)}>
              Open: {r.name} <span className="muted" style={{ marginLeft: 6 }}>({r.dbId})</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

const defaultTiles = [
  { to: "/inventory", label: "Inventory", sub: "All records", icon: Boxes, disabled: false },
  { to: "/admin/customers/details", label: "Customers", sub: "All records", icon: UsersRound, disabled: false },
  { to: "/demo",     label: "Demo Fleet",  sub: "Show units", icon: Car, disabled: false },
  { to: "/stock",    label: "Stock",       sub: "Available now", icon: Warehouse, disabled: false },
  { to: "/rentals",  label: "Rentals",     sub: "Rental fleet", icon: BadgeDollarSign, disabled: false },
  { to: "/sold",     label: "Sold",        sub: "Closed deals", icon: ListChecks, disabled: false },
  { to: "/pdi",      label: "Start PDI",   sub: "In Stock - Calgary", icon: ListChecks, disabled: false },
  { to: "/admin",    label: "serviceFlow.", sub: "IMS + Service Dept. integration", icon: LucideDollarSign, disabled: true },
  { to: "/scheduler",    label: "scheduleFlow.", sub: "IMS + Service Scheduling", icon: LucideDollarSign, disabled: true },
  { to: "/vin",      label: "Scan VIN",    sub: "Quick lookup", icon: ScanLine, disabled: false },
  { to: "/driver",   label: "Driver",      sub: "Delivery flow", icon: Truck, disabled: false },
  { to: "/new",      label: "Add Record",  sub: "New unit", icon: PlusCircle, disabled: false },
  { to: "/reports",  label: "Reports",     sub: "Stats & Analytics", icon: BarChart3, disabled: false },
  { to: "/admin",    label: "Admin Tools", sub: "Tools & Settings", icon: Shield, disabled: true },
];

function Splash() {
  const nav = useNavigate();

  // Later you can load this from localStorage / user profile
  const [tiles, setTiles] = useState(defaultTiles);

  return (
    <div className="container">
      <div className="splash">
        <div>
          <div className="splash-title">Inventory Management</div>
          <div className="splash-sub">React • SQLite • Python • JS</div>

          <div className="splash-grid">
            {tiles.map((t) => {
              const Icon = t.icon;

              if (t.disabled) {
                return (
                  <div
                    key={t.to}
                    className="scan-tile scan-tile-disabled"
                    title="Not available in your plan / role"
                  >
                    <div className="scan-tile-top">
                      <div className="scan-icon">
                        <Icon size={22} />
                      </div>
                    </div>
                    <div className="scan-title">{t.label}</div>
                    <div className="scan-sub">{t.sub}</div>
                  </div>
                );
              }

              return (
                <button
                  key={t.to}
                  className="scan-tile"
                  onClick={() => nav(t.to)}
                >
                  <div className="scan-tile-top">
                    <div className="scan-icon">
                      <Icon size={22} />
                    </div>
                    <div className="scan-chev">↗</div>
                  </div>
                  <div className="scan-title">{t.label}</div>
                  <div className="scan-sub">{t.sub}</div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function WithNav({ children }) {
  const loc = useLocation();
  // Hide navbar on splash + auth screens
  const hide = loc.pathname === "/" || loc.pathname === "/login" || loc.pathname === "/db-select";
  return (
    <>
      {!hide && <NavBar />}
      {children}
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthGate>
          <WithNav>
            <Routes>
              <Route path="/" element={<PreLoginSplashPage />} />  
              <Route path="/home" element={<Splash />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/db-select" element={<DbSelectPage />} />
              <Route path="/inventory" element={<InventoryPage />} />
              <Route path="/demo" element={<DemoPage />} />
              <Route path="/rentals" element={<RentalPage />} />
              <Route path="/stock" element={<StockPage />} />
              <Route path="/sold" element={<SoldPage />} />
          <Route path="/vin" element={<VinScanPage />} />
          <Route path="/pdi/:id" element={<PdiPage />} />
          <Route path="/pdi/templates" element={<PdiTemplatePage />} />
          <Route path="/pdi" element={<PdiSelectPage />} />
              <Route path="/driver" element={<DriverPage />} />
              <Route path="/admin" element={<AdminPage />} />
              <Route path="/new" element={<NewRecordPage />} />
              <Route path="/builder-upload" element={<BuilderUploadPage />} />
              <Route path="/detail/:table/:id" element={<DetailPage />} />
              <Route path="/reports" element={<ReportsPage />} />
              <Route path="/admin/import" element={<InventoryImportPage />} />
              <Route path="/admin/schema" element={<AdminSchemaPage />} />
              <Route path="/admin/users" element={<UsersPage />} />
              <Route path="/admin/dbs" element={<DatabaseSettingsPage />} />
              <Route path="/admin/devtools" element={<DeveloperToolsPage />} />
              <Route path="/admin/moves" element={<MovesPage />} />
              <Route path="/admin/driver-mgmt" element={<DriverMgmtPage />} />
              <Route path="/admin/customers" element={<AdminCustomersPage />} />
              <Route path="/admin/vendors" element={<AdminVendorsPage />} />
              <Route path="/admin/customers/details" element={<CustomerDetailsPage />} />
              <Route path="/admin/api-keys" element={<AdminApiKeysPage />} />
              <Route path="/active-desktop" element={<ActiveDesktop />} />
              <Route path="/scheduler" element={<SchedulerPage />} />
              <Route path="*" element={<div className="container">Route Not found.</div>} />
            </Routes>
          </WithNav>
        </AuthGate>
      </ThemeProvider>
    </BrowserRouter>
  );
}
